
import { Component, Vue, Prop, Watch, Emit } from "vue-property-decorator";
import { namespace, Getter, Mutation, Action, State } from "vuex-class";
import { get, clone, cloneDeep } from "lodash";
import { GuardTour, GuardTourStep } from "@/store/guard-tours/types";
import { MapItemData } from "@/store/map-layers/types";
import { FeaturesList } from "@/store/types";
import ActionPlanLinker from "@/components/guard-tours/ActionPlanLinker.vue";
import TourStepItem from "@/components/guard-tours/TourStepItem.vue";
import AssessmentTourFilter from "@/components/guard-tours/AssessmentTourFilter.vue";
import TourEditor from "@/components/guard-tours/TourEditor.vue";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import MapLayerItemTypes from "@/constants/MapLayerItemTypes";
import vSelect from "vue-select";
import draggable from "vuedraggable";

const GuardTours = namespace("GuardTours");
const MapLayers = namespace("mapLayers");
const Tasks = namespace("tasks"); 
const SiteMonitor = namespace("siteMonitor");

@Component({
	components: {
        "action-plan-linker": ActionPlanLinker,
        "tour-step-item": TourStepItem,
        "assessment-tour-filter": AssessmentTourFilter,
        "tour-editor": TourEditor,
		"vue-perfect-scrollbar": VuePerfectScrollbar,
        "v-select": vSelect,
        draggable,
	}
})
export default class GuardTourStepEditor extends Vue {
    @GuardTours.Action updateGuardTour;
    @GuardTours.Action fetchGuardTour;
    @GuardTours.Action fetchGuardTours;
    @GuardTours.Action deleteGuardTour;

    @GuardTours.Mutation setCurrentGuardTour: (currentGuardTour: GuardTour) => void;
    @GuardTours.Mutation setCurrentGuardTourSteps: (guardTourSteps: GuardTourStep[]) => void;
    @GuardTours.Mutation setCurrentGuardTourTitle: (title: string) => void;
    
    @GuardTours.State selectedAreaId: number;
    @GuardTours.State currentGuardTour: GuardTour;
    @GuardTours.Getter("getGuardTours") guardTours: Array<GuardTour>;
    @GuardTours.Getter getGuardTour: (guardTourId: number) => GuardTour;    

    @Getter("getFeaturesList") featuresList: FeaturesList;

    @Tasks.Action fetchActionPlans: () => Promise<void>;    

    public selectedGuardTour: GuardTour = null;
    public showDeleteModal: boolean = false;
    public saveButtonsEnabled: boolean = false;
    private tourType: number = 1;

    private get isGuardToursEnabled(): boolean {
		return get(this.featuresList, ["Mobile", "GuardTours"]);
	}

    @Emit("go-to-elevation")
    private goToElevation(minElevation: number){}

    @Emit("tour-updated-or-cancelled")
    public emitTourUpdatedOrCancelled() {}

    @Watch("selectedAreaId")
    private onSelectedAreaIdChanged() {
        this.selectedGuardTour = null;
    }

    @Watch("currentGuardTour", {deep:true})
    public onCurrentGuardTourChanged(value: GuardTour, oldValue: GuardTour): void {        
        this.saveButtonsEnabled = true;
    }

    @Watch("selectedGuardTour", {deep:true})
    public async onSelectedGuardTourOptionChange(guardTour): Promise<void> {
        if (guardTour) {
            await this.fetchGuardTour(guardTour.guardTourId);

            if (this.currentGuardTour.guardTourSteps) {
                // Load all action plans for this tour
                await this.fetchActionPlans(); 
                if (
                    this.currentGuardTour.guardTourSteps[0] &&
                    this.currentGuardTour.guardTourSteps[0].minElevation
                ) {
                    // change map to the layer of the first tour step
                    this.goToElevation(this.currentGuardTour.guardTourSteps[0].minElevation);
                }
            }
        } else {
            this.setCurrentGuardTour(null);
        }

        this.saveButtonsEnabled = false;
    }

    public async tourUpdated(tour: GuardTour) {        
        // Make sure that we select this new tour so we can start editing
        this.selectedGuardTour = tour;
        await this.fetchGuardTours(this.selectedAreaId);
    }
    
    public async saveGuardTour(): Promise<void> {
        // Save and update current tour
        await this.updateGuardTour(this.currentGuardTour);
        await this.fetchGuardTour(this.currentGuardTour.guardTourId);
        this.selectedGuardTour = this.currentGuardTour;

        this.emitTourUpdatedOrCancelled();

        this.saveButtonsEnabled = false;
    }

    public async cancelGuardTour(): Promise<void> {
        // remove any waypoints being created from the map
        this.emitTourUpdatedOrCancelled();

        await this.fetchGuardTour(this.currentGuardTour.guardTourId);

        this.saveButtonsEnabled = false;
    }

    public async deleteGuardTourHandler() {
        this.deleteGuardTour(this.currentGuardTour.guardTourId);
        this.showDeleteModal = false;
        this.selectedGuardTour = null;
    }

    // Guard tour steps getter (avoid mutating collection outside of VueX)
    private get guardTourSteps() {
        return this.currentGuardTour.guardTourSteps;
    }

    // Guard tour steps setter (avoid mutating collection outside of VueX)
    private set guardTourSteps(value) {
        this.setCurrentGuardTourSteps(value);
    }

    // Guard tour title getter (avoid mutating collection outside of VueX)
    private get guardTourTitle() {
        return this.currentGuardTour.title;
    }

    // Guard tour title setter (avoid mutating collection outside of VueX)
    private set guardTourTitle(value) {
        this.setCurrentGuardTourTitle(value);               
    }

    public get actionPlanDragOptions() {
		return {
			animation: 200,
			disabled: false,
			ghostClass: "action-plan-item-ghost"
		};
	}

    public filterTourTypeChanged() {
        this.selectedGuardTour = null;
    }
}
